import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { attorneyInfoService, AttorneyInfoRec } from '../../../../services/attorneyInfo';
import { vaultService } from '../../../../services/vaultService';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('AttorneyInfoData');

type RouteParams = {
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const AttorneyInfoDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();

  const [attorneysData, setAttorneysData] = useState<AttorneyInfoRec[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadAttorneysData();
  }, [targetUserId]);

  const loadAttorneysData = async () => {
    try {
      setLoading(true);
      const data = await attorneyInfoService.getAttorneyInfo(targetUserId);
      logger.debug('[Attorneys Data Debug] Loaded all attorney entries:', data);

      if (data && Array.isArray(data) && data.length > 0) {
        // Load portal credentials for all attorneys (only for the owner, not viewOnly mode)
        const attorneysWithCredentials = await Promise.all(
          data.map(async (attorney) => {
            // Skip loading portal credentials in viewOnly mode - they are protected until EOL
            if (!viewOnly || isDeathVerifiedForSelectedUser) {
              try {
                logger.debug('[Portal Debug] Attempting to load portal credentials for attorney ID:', attorney.id);
                const portalData = await vaultService.getPortalCredentials(attorney.id, 'attorney');
                if (portalData) {
                  return {
                    ...attorney,
                    username: portalData.username,
                    password: portalData.password,
                  };
                }
              } catch (vaultError) {
                logger.debug('[Portal Debug] Error loading portal data for attorney:', attorney.id, vaultError);
              }
            }
            return attorney;
          })
        );

        logger.debug('[Attorneys Data Debug] Setting all attorney data entries:', attorneysWithCredentials);
        setAttorneysData(attorneysWithCredentials);
      } else {
        logger.debug('[Attorneys Data Debug] No attorney entries found or invalid data format');
        setAttorneysData([]);
      }
    } catch (error) {
      logger.error('Error loading attorneys data:', error);
      Alert.alert('Error', 'Failed to load attorney information');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const handleEditAttorney = (attorney: AttorneyInfoRec) => {
    navigation.navigate('AttorneyInfoInput', {
      attorneyId: attorney.id,
      isEdit: true
    });
  };

  const handleDeleteAttorney = async (attorney: AttorneyInfoRec) => {
    Alert.alert('Delete', 'Are you sure you want to delete this attorney entry?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            await attorneyInfoService.deleteAttorneyInfo(attorney.id);

            // Reload data after deletion
            await loadAttorneysData();

            // Check if there are any remaining attorney entries
            const remainingData = await attorneyInfoService.getAllAttorneyInfo();
            if (!remainingData || remainingData.length === 0) {
              await tasksService.resetTask('attorney');
              navigation.navigate('FinanceChecklist');
            }

            Alert.alert('Deleted', 'Attorney information deleted.');
          } catch (e) {
            logger.error('Delete attorney failed:', e);
            Alert.alert('Error', 'Failed to delete attorney information');
          }
        }
      },
    ]);
  };

  const handleAddAttorney = () => {
    navigation.navigate('AttorneyInfoInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Attorney Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading attorney information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!attorneysData || attorneysData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Attorney Information</Text>

        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {viewOnly ? "This user has not added attorney information yet." : "No attorney information found"}
            </Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddAttorney}
              >
                <Text style={styles.addButtonText}>Add Attorney Info</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Attorney Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {attorneysData.map((attorney, index) => (
            <View key={attorney.id || index} style={styles.attorneyCard}>
              <View style={styles.cardHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.cardName}>
                    {attorney.attorneyName || attorney.firmName || 'Attorney'}
                  </Text>
                  <Text style={styles.cardType}>
                    {attorney.firmName && attorney.attorneyName ? attorney.firmName : 'Firm not specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editCardButton}
                      onPress={() => handleEditAttorney(attorney)}
                    >
                      <Text style={styles.editCardButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteCardButton}
                      onPress={() => handleDeleteAttorney(attorney)}
                    >
                      <Text style={styles.deleteCardButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              <InfoItem label="Firm Name" value={attorney.firmName || ''} />
              <InfoItem label="Attorney Name" value={attorney.attorneyName || ''} />
              <InfoItem label="Specialization" value={attorney.specialization || ''} />
              <InfoItem label="Office Phone" value={formatPhoneNumber(attorney.officePhone || '')} />
              <InfoItem label="Cell Phone" value={formatPhoneNumber(attorney.cellPhone || '')} />
              <InfoItem label="Email" value={attorney.email || ''} />
              <InfoItem label="Office Address" value={attorney.officeAddress || ''} />
              <InfoItem label="Website" value={attorney.website || ''} />
              <InfoItem label="Bar Number" value={attorney.barNumber || ''} />

              {attorney.username && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Username</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>
                      {showUsername[attorney.id] ? attorney.username : '••••••••••'}
                    </Text>
                    <TouchableOpacity
                      onPress={() => setShowUsername(prev => ({ ...prev, [attorney.id]: !prev[attorney.id] }))}
                      style={styles.eyeButton}
                    >
                      <Image
                        source={showUsername[attorney.id]
                          ? require('../../../../assets/icons/Eye.png')
                          : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}

              {attorney.password && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Password</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>
                      {showPassword[attorney.id] ? attorney.password : '••••••••••'}
                    </Text>
                    <TouchableOpacity
                      onPress={() => setShowPassword(prev => ({ ...prev, [attorney.id]: !prev[attorney.id] }))}
                      style={styles.eyeButton}
                    >
                      <Image
                        source={showPassword[attorney.id]
                          ? require('../../../../assets/icons/Eye.png')
                          : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}

              {attorney.hourlyRate && (
                <InfoItem label="Hourly Rate" value={`$${attorney.hourlyRate}`} />
              )}
              {attorney.retainerAmount && (
                <InfoItem label="Retainer Amount" value={`$${attorney.retainerAmount}`} />
              )}
              <InfoItem label="Services Provided" value={attorney.servicesProvided || ''} />
              <InfoItem label="Notes" value={attorney.notes || ''} />
            </View>
          ))}

          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherAttorneyButton}
                onPress={handleAddAttorney}
              >
                <Text style={styles.addAnotherAttorneyButtonText}>Add Another Attorney</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.navigate('FinanceChecklist')}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  attorneyCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  cardName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  cardType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editCardButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editCardButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteCardButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteCardButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherAttorneyButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherAttorneyButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});
